// src/pages/CaseStudyDetailPage.jsx
import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams, Link } from "react-router-dom";
import { fetchCaseStudies } from "../api/caseStudyApi";
import { fetchBlogCategories } from "../api/blogCategoryApi";
import { toastError } from "../utils/toast";
import { withBase } from "../config/env";
import "./css/blogdetail.css"; // reuse detail + sidebar styles (same as blog detail)
// If your shimmer helpers live in blogs.css, you can also import it:
// import "./css/blogs.css";

/* --- Optional sidebar shimmers (use if you already have shimmer CSS) --- */
function SidebarCatsShimmer() {
  return (
    <div className="cat-list" aria-hidden="true">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="shimmer-pill" />
      ))}
    </div>
  );
}
function RecentPostsShimmer() {
  return (
    <ul className="recent-posts-list" aria-hidden="true">
      {Array.from({ length: 3 }).map((_, i) => (
        <li key={i} className="list-tile">
          <div className="list-tile-link shimmer-tile">
            <div className="list-tile-media shimmer-block" />
            <div className="list-tile-body">
              <div className="shimmer-line small" />
              <div className="shimmer-line tiny" />
              <div className="shimmer-line tiny short" />
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function CaseStudyDetailPage() {
  const { id } = useParams();
  const { state } = useLocation();

  // accept either state.caseStudy or state.blog to be safe (older code passed blog)
  const initial = state?.caseStudy || state?.blog || null;

  const [item, setItem] = useState(initial);
  const [loading, setLoading] = useState(!initial);

  // for sidebar
  const [categories, setCategories] = useState([]); // only "Case Studies"
  const [allCases, setAllCases] = useState([]);
  const [catsLoading, setCatsLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);

  // Load the single case study if not passed via state
  useEffect(() => {
    if (initial) return; // already have it
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const all = await fetchCaseStudies();
        const found =
          Array.isArray(all) &&
          all.find(
            (b) =>
              String(b?.id) === String(id) ||
              String(b?._id) === String(id) ||
              String(b?.slug) === String(id)
          );
        if (mounted) setItem(found || null);
        if (!found) toastError("Case study not found.");
      } catch {
        toastError("Failed to load case study.");
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id, initial]);

  // Load categories (type = "Case Studies") and all case studies (for recent)
  useEffect(() => {
    let on = true;
    (async () => {
      try {
        setCatsLoading(true);
        const catsRes = await fetchBlogCategories();
        const list = Array.isArray(catsRes) ? catsRes : catsRes?.data || [];
        const caseOnly = list.filter(
          (c) => (c?.type || "").toLowerCase() === "case studies"
        );
        if (on) setCategories(caseOnly);
      } catch {
        setCategories([]);
      } finally {
        setCatsLoading(false);
      }

      try {
        setListLoading(true);
        const res = await fetchCaseStudies();
        if (on) setAllCases(Array.isArray(res) ? res : []);
      } catch {
        // ignore but keep empty
      } finally {
        setListLoading(false);
      }
    })();
    return () => {
      on = false;
    };
  }, []);

  // Build inline categories for the current item (safe if item is null)
  const inlineCategories = useMemo(() => {
    const b = item || {};
    if (Array.isArray(b.categories) && b.categories.length) return b.categories;
    if (Array.isArray(b.tags) && b.tags.length) return b.tags;
    if (b.category_name) return [b.category_name];
    return [];
  }, [item]);

  // Recent 3 case studies (exclude current one)
  const recent = useMemo(() => {
    const getDate = (x) =>
      new Date(x?.created_at || x?.createdAt || x?.date || 0).getTime();
    return [...allCases]
      .filter(
        (b) => String(b?.id ?? b?._id ?? b?.slug) !== String(id)
      )
      .sort((a, b) => getDate(b) - getDate(a))
      .slice(0, 3);
  }, [allCases, id]);

  // Helpers
  const getExcerpt = (html, len = 80) => {
    if (!html) return "";
    const txt = String(html).replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
    return txt.length > len ? txt.slice(0, len) + "…" : txt;
  };

  // Early-return loading (main content)
  if (loading) {
    return (
      <main className="blogs-wrap">
        <section className="blogs-section">
          <div className="blogs-loading">
            <div className="loading-spinner"></div>
            <p>Loading case study…</p>
          </div>
        </section>
      </main>
    );
  }

  if (!item) {
    return (
      <main className="blogs-wrap">
        <section className="blogs-section">
          <div className="blogs-empty">
            <h3>Case Study Not Found</h3>
            <p>The requested case study doesn't exist or may have been removed.</p>
            <Link to="/case-studies" className="blog-readmore">
              ← Back to Case Studies
            </Link>
          </div>
        </section>
      </main>
    );
  }

  // Safe fields
  const title = item?.title || "Untitled";
  const thumbnail = withBase(item?.thumbnail);
  const createdAt = item?.created_at || item?.createdAt || item?.date;
  const content = item?.content || item?.body || "";

  return (
    <main className="blogs-wrap">
      <div className="blog-detail-grid">
        {/* MAIN */}
        <article className="blog-detail">
          <header className="blog-detail-header">
            <div className="blog-meta">
              {inlineCategories.length > 0 && (
                <div className="blog-categories">
                  {inlineCategories.slice(0, 3).map((category, index) => {
                    const catObj = categories.find((c) => c.title === category);
                    const catId = catObj?.id;
                    return (
                      <Link
                        key={index}
                        to={
                          catId
                            ? `/case-studies?category=${catId}&categoryName=${encodeURIComponent(
                                category
                              )}`
                            : `/case-studies?categoryName=${encodeURIComponent(
                                category
                              )}`
                        }
                        className="blog-category"
                      >
                        {category}
                      </Link>
                    );
                  })}
                </div>
              )}

              <h1 className="blog-detail-title">{title}</h1>

              <div className="blog-author-date">
                {createdAt && (
                  <time className="blog-detail-date">
                    {new Date(createdAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </time>
                )}
              </div>
            </div>

            {thumbnail && (
              <div className="blog-detail-cover">
                <img
                  src={thumbnail}
                  alt={title}
                  className="blog-thumbnail"
                  loading="lazy"
                />
              </div>
            )}
          </header>

          <section className="blog-detail-content">
            {/* Render HTML if provided */}
            {/<[a-z][\s\S]*>/i.test(String(content)) ? (
              <div
                className="blog-html"
                dangerouslySetInnerHTML={{ __html: content }}
              />
            ) : (
              <div className="blog-text">
                {String(content)
                  .split("\n")
                  .map((paragraph, i) => (
                    <p key={i}>{paragraph}</p>
                  ))}
              </div>
            )}
          </section>

          <footer className="blog-detail-footer">
            <div className="blog-tags">
              {inlineCategories.length > 0 && (
                <>
                  <span>Tags:</span>
                  {inlineCategories.map((category, index) => (
                    <span key={index} className="blog-tag">
                      {category}
                    </span>
                  ))}
                </>
              )}
            </div>

            <div
              className="blog-actions"
              style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 12 }}
            >
              <Link to="/case-studies" className="blog-readmore">
                ← Back to All Case Studies
              </Link>
              <Link to="/contact" className="blog-readmore">
                Inquiry Now →
              </Link>
            </div>
          </footer>
        </article>

        {/* SIDEBAR */}
        <aside className="blog-detail-aside" aria-label="Sidebar">
          <div className="sidebar-card">
            <h3 className="sidebar-title">Categories</h3>
            {catsLoading ? (
              <SidebarCatsShimmer />
            ) : (
              <div className="cat-list">
                <Link to="/case-studies" className="cat-item">
                  <span>All</span>
                </Link>
                {categories.map((c) => (
                  <Link
                    key={c.id ?? c.title}
                    to={`/case-studies?category=${c.id}&categoryName=${encodeURIComponent(
                      c.title
                    )}`}
                    className="cat-item"
                  >
                    <span>{c.title}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="sidebar-card">
            <h3 className="sidebar-title small">Recent Case Studies</h3>
            {listLoading ? (
              <RecentPostsShimmer />
            ) : (
              <ul className="recent-posts-list">
                {recent.map((b) => {
                  const bid = b?.id ?? b?._id ?? b?.slug;
                  const btitle = b?.title || "Untitled";
                  const cover = withBase(b?.thumbnail);
                  const bdate = b?.created_at || b?.createdAt || b?.date;
                  const subtitle = getExcerpt(b?.excerpt || b?.content, 80);

                  return (
                    <li key={`recent-${bid}`} className="list-tile">

<Link  to={`/case-studies/${bid}`} className="list-tile-link">
                      <div className="list-tile-media">
                        {cover ? (
                          <img src={cover} alt={btitle} />
                        ) : (
                          <div className="list-tile-placeholder" />
                        )}
                      </div>
                      <div className="list-tile-body">
                        <h4 className="list-tile-title">{btitle}</h4>
                        {subtitle && (
                          <p className="list-tile-subtitle">{subtitle}</p>
                        )}
                        {bdate && (
                          <time className="list-tile-date">
                            {new Date(bdate).toLocaleDateString()}
                          </time>
                        )}
                      </div>
                    </Link>
                      
                    </li>
                  );
                })}
                {recent.length === 0 && (
                  <li className="recent-empty">No recent items</li>
                )}
              </ul>
            )}
          </div>
        </aside>
      </div>
    </main>
  );
}
